<template>
    <PageHeader :crumbs="[{ label: $t('portal.customers.title'), to: { name: 'admin.customers' } }, { label: customer.name || '...' }]" />

    <div class="tw-p-2 tw-space-y-4" v-if="!loading">
        <!-- Customer info -->
        <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-overflow-hidden">
            <div class="tw-p-5">
                <!-- Avatar + name + actions -->
                <div class="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
                    <div class="tw-flex tw-items-center tw-gap-4">
                        <div class="tw-h-16 tw-w-16 tw-shrink-0 tw-rounded-2xl tw-bg-gradient-to-tr tw-from-primary-500 tw-to-primary-700 tw-shadow-lg tw-shadow-primary-500/30 tw-flex tw-items-center tw-justify-center">
                            <span class="tw-text-white tw-text-xl tw-font-bold tw-uppercase">{{ initials }}</span>
                        </div>
                        <div class="tw-min-w-0">
                            <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                <h1 class="tw-text-xl tw-font-bold tw-text-gray-800 tw-truncate">{{ customer.name }}</h1>
                                <StatusBadge :status="customer.status" />
                                <PaymentBadge :status="customer.payment_status" />
                            </div>
                            <p class="tw-text-sm tw-text-gray-400 tw-flex tw-items-center tw-gap-1.5 tw-truncate tw-mt-0.5">
                                <icon icon="solar:letter-bold-duotone" class="tw-text-primary-400 tw-shrink-0" />
                                {{ customer.email || '—' }}
                            </p>
                        </div>
                    </div>
                    <button @click="editVisible = true"
                        class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-primary-600 tw-bg-primary-50 tw-border tw-border-solid tw-border-primary-200 tw-duration-200 hover:tw-bg-primary-600 hover:tw-text-white hover:tw-border-primary-600">
                        <icon icon="solar:pen-2-bold-duotone" class="tw-text-base" />
                        {{ $t('buttons.edit') }}
                    </button>
                </div>

                <!-- Info chips -->
                <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-mt-5">
                    <!-- Phone -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-100">
                        <div class="tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-lg tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:phone-bold-duotone" class="tw-text-primary-500" />
                        </div>
                        <div class="tw-min-w-0">
                            <p class="tw-text-[11px] tw-text-gray-400 tw-uppercase tw-tracking-wide">{{ $t('fields.customer_phone') }}</p>
                            <div v-if="customer.phone" class="tw-flex tw-items-center tw-gap-2" dir="ltr">
                                <span class="tw-font-semibold tw-text-gray-700 tw-text-sm tw-truncate">{{ customer.phone }}</span>
                                <a :href="waLink(customer.phone)" target="_blank" rel="noopener" :title="$t('buttons.whatsapp')"
                                    class="tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded-md tw-bg-emerald-50 tw-text-emerald-600 tw-border tw-border-solid tw-border-emerald-200 tw-duration-200 hover:tw-bg-emerald-500 hover:tw-text-white">
                                    <icon icon="mdi:whatsapp" class="tw-text-sm" />
                                </a>
                            </div>
                            <p v-else class="tw-font-semibold tw-text-gray-400 tw-text-sm">—</p>
                        </div>
                    </div>

                    <!-- Country -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-100">
                        <div class="tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-lg tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:map-point-bold-duotone" class="tw-text-primary-500" />
                        </div>
                        <div class="tw-min-w-0">
                            <p class="tw-text-[11px] tw-text-gray-400 tw-uppercase tw-tracking-wide">{{ $t('portal.fields.country') }}</p>
                            <p class="tw-font-semibold tw-text-gray-700 tw-text-sm tw-truncate">{{ customer.country || '—' }}</p>
                        </div>
                    </div>

                    <!-- Portal access -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-100">
                        <div class="tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-lg tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:shield-user-bold-duotone" class="tw-text-primary-500" />
                        </div>
                        <div class="tw-min-w-0">
                            <p class="tw-text-[11px] tw-text-gray-400 tw-uppercase tw-tracking-wide">{{ $t('portal.fields.portal') }}</p>
                            <p class="tw-font-semibold tw-text-sm tw-truncate" :class="customer.status !== 'disabled' ? 'tw-text-emerald-600' : 'tw-text-rose-600'">
                                {{ customer.status !== 'disabled' ? $t('portal.fields.enabled') : $t('portal.fields.disabled') }}
                            </p>
                        </div>
                    </div>

                    <!-- Notes -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-100">
                        <div class="tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-lg tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:notes-bold-duotone" class="tw-text-primary-500" />
                        </div>
                        <div class="tw-min-w-0">
                            <p class="tw-text-[11px] tw-text-gray-400 tw-uppercase tw-tracking-wide">{{ $t('fields.customer_notes') }}</p>
                            <p class="tw-font-semibold tw-text-gray-700 tw-text-sm tw-truncate" :title="customer.note || ''">{{ customer.note || '—' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Portal actions -->
                <div class="tw-mt-4 tw-pt-4 tw-border-t tw-border-solid tw-border-gray-100 tw-flex tw-flex-wrap tw-gap-2">
                    <button v-if="customer.status === 'disabled'" @click="openPortal('enable')"
                        class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-emerald-600 tw-bg-emerald-50 tw-border tw-border-solid tw-border-emerald-200 tw-duration-200 hover:tw-bg-emerald-600 hover:tw-text-white hover:tw-border-emerald-600">
                        <icon icon="solar:play-circle-bold-duotone" class="tw-text-base" />
                        {{ $t('portal.customers.enable-portal') }}
                    </button>
                    <button v-else @click="openPortal('disable')"
                        class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-rose-600 tw-bg-rose-50 tw-border tw-border-solid tw-border-rose-200 tw-duration-200 hover:tw-bg-rose-600 hover:tw-text-white hover:tw-border-rose-600">
                        <icon icon="solar:pause-circle-bold-duotone" class="tw-text-base" />
                        {{ $t('portal.customers.disable-portal') }}
                    </button>
                    <button @click="openPortal('password')"
                        class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-primary-600 tw-bg-primary-50 tw-border tw-border-solid tw-border-primary-200 tw-duration-200 hover:tw-bg-primary-600 hover:tw-text-white hover:tw-border-primary-600">
                        <icon icon="solar:lock-password-bold-duotone" class="tw-text-base" />
                        {{ $t('portal.customers.edit-password') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Assigned products -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-4">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <h3 class="tw-font-semibold tw-text-gray-700">{{ $t('portal.customers.assigned-products') }}</h3>
                <a-button left-icon="heroicons:plus-16-solid" @click="openAssign()">{{ $t('portal.purchases.assign') }}</a-button>
            </div>
            <DataTable :columns="purchaseColumns" :count="(customer.purchases || []).length" :empty-text="$t('portal.customers.no-products')">
                <tr v-for="p in customer.purchases" :key="p.id" :class="rowClass">
                    <td :class="cellClass"><span class="tw-font-medium tw-text-gray-800">{{ p.product_name }}</span></td>
                    <td :class="cellClass">
                        <span v-if="p.code" class="tw-inline-flex tw-items-center tw-gap-1 tw-font-mono tw-text-xs tw-font-semibold tw-text-primary-700 tw-bg-primary-50 tw-border tw-border-solid tw-border-primary-200 tw-rounded tw-px-1.5 tw-py-0.5">{{ p.code }}</span>
                        <span v-else class="tw-text-gray-400">—</span>
                    </td>
                    <td :class="cellClass"><span class="tw-capitalize">{{ p.product_type }}</span></td>
                    <td :class="cellClass">{{ fmt.currency(p.price) }}</td>
                    <td :class="cellClass">{{ fmt.date(p.start_date) }}</td>
                    <td :class="cellClass">{{ p.expiry_date ? fmt.date(p.expiry_date) : $t('common.without') }}</td>
                    <td :class="cellClass"><StatusBadge :status="p.status" /></td>
                    <td :class="cellClass"><span class="tw-text-gray-500 tw-block tw-max-w-[180px] tw-truncate" :title="p.note || ''">{{ p.note || '—' }}</span></td>
                    <td :class="cellClass">
                        <div class="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                            <a-action-button icon="solar:pen-2-bold-duotone" intent="secondary" @click="openAssign(p)" />
                            <a-action-button icon="solar:trash-bin-2-bold-duotone" intent="danger" @click="askDelete(p)" />
                        </div>
                    </td>
                </tr>
            </DataTable>
        </div>
    </div>

    <div v-else class="tw-p-8 tw-text-center tw-text-gray-400"><icon icon="line-md:loading-twotone-loop" class="tw-text-3xl" /></div>

    <CustomerFormModal :visible="editVisible" :customer="customer" @cancel="editVisible = false" @saved="onCustomerSaved" />
    <AssignProductModal :visible="assignVisible" :customer-id="id" :purchase="editingPurchase" @cancel="assignVisible = false" @saved="onAssigned" />
    <PortalAccessModal :visible="portalVisible" :customer-id="id" :mode="portalMode" @cancel="portalVisible = false" @saved="onPortalSaved" />
    <DeleteModal :visible="deleteVisible" :loading="deleting" @cancel="deleteVisible = false" @confirm="confirmDelete" />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { show } from '@/api/customers';
import { remove as removePurchase } from '@/api/purchases';
import PageHeader from '@/components/PageHeader.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import PaymentBadge from '@/components/PaymentBadge.vue';
import DataTable from '@/components/table/DataTable.vue';
import DeleteModal from '@/components/modals/DeleteModal.vue';
import CustomerFormModal from './CustomerFormModal.vue';
import AssignProductModal from './AssignProductModal.vue';
import PortalAccessModal from './PortalAccessModal.vue';
import { useFormat } from '@/composables/useFormat';
import { rowClass, cellClass } from '@/composables/useTableStyles';
import useAlert from '@/composables/useAlert';

const route = useRoute();
const i18n = useI18n();
const fmt = useFormat();
const id = route.params.id;

const purchaseColumns = computed(() => [
    { label: i18n.t('fields.product') },
    { label: i18n.t('portal.fields.code'), classes: ['tw-w-[130px]'] },
    { label: i18n.t('portal.fields.type'), classes: ['tw-w-[110px]'] },
    { label: i18n.t('fields.price'), classes: ['tw-w-[100px]'] },
    { label: i18n.t('portal.fields.start-date'), classes: ['tw-w-[120px]'] },
    { label: i18n.t('portal.fields.expiry-date'), classes: ['tw-w-[130px]'] },
    { label: i18n.t('fields.status'), classes: ['tw-w-[100px]'] },
    { label: i18n.t('fields.customer_notes'), classes: ['tw-w-[160px]'] },
    { label: i18n.t('fields.actions'), classes: ['tw-w-[100px]', 'tw-text-end'] },
]);

const waLink = (phone) => `https://wa.me/${String(phone || '').replace(/[^\d]/g, '')}`;

const initials = computed(() => {
    const name = customer.value?.name || '';
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('') || 'C';
});

const customer = ref({ name: '', purchases: [] });
const loading = ref(true);

const editVisible = ref(false);
const assignVisible = ref(false);
const editingPurchase = ref(null);
const portalVisible = ref(false);
const portalMode = ref('enable');
const deleteVisible = ref(false);
const deleting = ref(false);
const toDelete = ref(null);

async function load() {
    loading.value = true;
    try {
        const { data } = await show(id);
        customer.value = data.customer;
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

const onCustomerSaved = () => { editVisible.value = false; load(); };

const openAssign = (p = null) => { editingPurchase.value = p; assignVisible.value = true; };
const onAssigned = () => { assignVisible.value = false; load(); };

const openPortal = (mode) => { portalMode.value = mode; portalVisible.value = true; };
const onPortalSaved = () => { portalVisible.value = false; load(); };

const askDelete = (p) => { toDelete.value = p; deleteVisible.value = true; };
const confirmDelete = async () => {
    deleting.value = true;
    try {
        await removePurchase(toDelete.value.id);
        useAlert(i18n.t('messages.deleted-successfully', { field: i18n.t('portal.entities.purchase') }));
        deleteVisible.value = false;
        load();
    } catch (e) { /* interceptor */ } finally {
        deleting.value = false;
    }
};

load();
</script>
